<template>
  <div
    class="grid grid-cols-4 flex flex-col flex-1 w-full overflow-hidden gap-4 mb-5"
  >
    <div
      class="bg-white p-5 rounded-xl flex flex-col flex-1 overflow-hidden border shadow-xl items-center gap-5 py-8"
    >
      <div class="relative">
        <Avatar
          class="bg-primary-100 !h-36 !w-36 cursor-pointer"
          :image="profileAvater ? profileAvater : require('@/assets/user.png')"
          shape="circle"
          size="large"
          @click="triggerUpload"
        />
        <div
          class="absolute bottom-0 right-0 bg-white text-xs rounded-full p-2 shadow-lg"
        >
          <v-icon>mdi-image-outline</v-icon>
        </div>
        <input
          type="file"
          ref="fileInput"
          class="hidden"
          accept="image/*"
          @change="handleFileUpload"
        />
        <div
          v-if="isUploading"
          class="absolute inset-0 flex items-center justify-center"
        >
          <div
            class="rounded-full bg-black bg-opacity-50 h-36 w-36 flex items-center justify-center"
          >
            <div class="text-white">{{ uploadProgress }}%</div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-2 items-center">
        <p class="text-xl text-primary-500">{{ profile?.fullname }}</p>
        <p class="bg-primary-100 rounded-lg px-2 py-1 text-sm text-primary-500">
          {{ userGroup }}
        </p>
      </div>
      <div class="flex flex-col gap-2 items-center">
        <p class="text-xs font-thin">
          {{ $t("profile.email") }}
        </p>
        <p>
          {{ !profile?.email || profile?.email === "" ? "-" : profile?.email }}
        </p>
      </div>
      <div class="flex flex-col gap-2 items-center">
        <p class="text-xs font-thin">
          {{ $t("profile.mobile_number") }}
        </p>
        <p>
          {{ !profile?.phone || profile?.phone === "" ? "-" : profile?.phone }}
        </p>
      </div>
      <div class="flex flex-col gap-2 items-center">
        <p class="text-xs font-thin">
          {{ $t("profile.join_date") }}
        </p>
        <p>
          {{
            !profile?.startdate || profile?.startdate === ""
              ? "-"
              : profile?.startdate
          }}
        </p>
      </div>
      <div class="flex flex-col flex-1 justify-end w-full gap-4">
        <Button
          :class="[
            'text-primary-900 hover:!bg-primary-900 hover:!text-white px-5 h-12 rounded-lg shadow-md gap-2 border border-primary-900',
            {
              'bg-primary-900 text-white': !passwordFormVisible,
              'bg-white': passwordFormVisible,
            },
          ]"
          type="button"
          :label="$t('profile.edit_profile')"
          @click="togglePasswordForm(false)"
        />
        <Button
          :class="[
            'text-primary-900 hover:!bg-primary-900 hover:!text-white px-5 h-12 rounded-lg shadow-md gap-2 border border-primary-900',
            {
              'bg-primary-900 text-white': passwordFormVisible,
              'bg-white': !passwordFormVisible,
            },
          ]"
          type="button"
          :label="$t('profile.reset_password')"
          @click="togglePasswordForm(true)"
        />
      </div>
    </div>
    <div
      v-if="!passwordFormVisible"
      class="col-span-3 bg-white p-5 rounded-xl flex flex-col flex-1 overflow-hidden border shadow-xl items-center gap-8"
    >
      <p class="text-xl text-primary-500">
        {{ $t("profile.edit_profile") }}
      </p>
      <Form
        v-slot="$form"
        ref="form"
        :resolver="resolver"
        :initialValues="initialValues"
        @submit="doUpdateProfile"
        class="flex justify-between flex-col w-full px-4 flex-1"
      >
        <div class="flex flex-col gap-8">
          <div class="flex flex-col gap-4">
            <label for="fullname">{{ $t("profile.full_name") }}</label>
            <InputText
              name="fullname"
              type="text"
              :placeholder="$t('profile.full_name')"
              class="bg-surface-100 rounded-lg py-3 px-4 font-thin text-sm"
              v-model="initialValues.fullname"
            />
            <Message
              v-if="$form.fullname?.invalid"
              severity="error"
              size="small"
              variant="simple"
              >{{ $form.fullname.error?.message }}</Message
            >
          </div>
          <div class="flex flex-col gap-4">
            <label for="email">{{ $t("profile.email") }}</label>
            <InputText
              name="email"
              type="text"
              :placeholder="$t('profile.email')"
              class="bg-surface-100 rounded-lg py-3 px-4 font-thin text-sm"
              v-model="initialValues.email"
            />
            <Message
              v-if="$form.email?.invalid"
              severity="error"
              size="small"
              variant="simple"
              >{{ $form.email.error?.message }}</Message
            >
          </div>
          <div class="flex flex-col gap-4">
            <label class="font-thin" for="phone">{{
              $t("profile.mobile_number")
            }}</label>
            <InputText
              name="phone"
              type="text"
              :placeholder="$t('profile.mobile_number')"
              class="bg-surface-100 rounded-md py-3 px-4 font-thin text-sm"
              v-model="initialValues.phone"
            />
            <Message
              v-if="$form.phone?.invalid"
              severity="error"
              size="small"
              variant="simple"
              >{{ $form.phone.error?.message }}</Message
            >
          </div>
        </div>
        <div class="flex justify-center gap-2">
          <Button
            class="bg-white text-primary-900 hover:!bg-primary-500 hover:!text-white px-5 h-12 rounded-lg shadow-md gap-2 border border-primary-900"
            type="submit"
            :label="$t('profile.update')"
          />
        </div>
      </Form>
    </div>
    <div
      v-else
      class="col-span-3 bg-white p-5 rounded-xl flex flex-col flex-1 overflow-hidden border shadow-xl items-center gap-8"
    >
      <p class="text-xl text-primary-500">
        {{ $t("profile.reset_password") }}
      </p>
      <Form
        v-slot="$form"
        ref="passwordForm"
        :resolver="passwordResolver"
        :initialValues="passwordInitialValues"
        @submit="doResetPassword"
        class="flex justify-between flex-col gap-5 w-full px-4 flex-1"
      >
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-4">
            <label for="old_password">{{ $t("profile.old_password") }}</label>
            <Password
              name="old_password"
              :placeholder="$t('profile.old_password')"
              class="bg-surface-100 rounded-lg py-3 px-4 font-thin text-sm"
              v-model="passwordInitialValues.old_password"
              :feedback="false"
              toggleMask
            />
            <Message
              v-if="$form.old_password?.invalid"
              severity="error"
              size="small"
              variant="simple"
              >{{ $form.old_password.error?.message }}</Message
            >
          </div>
          <div class="flex flex-col gap-4">
            <label for="new_password">{{ $t("profile.new_password") }}</label>
            <Password
              name="new_password"
              :placeholder="$t('profile.new_password')"
              class="bg-surface-100 rounded-lg py-3 px-4 font-thin text-sm"
              v-model="passwordInitialValues.new_password"
              :feedback="false"
              toggleMask
            />
            <Message
              v-if="$form.new_password?.invalid"
              severity="error"
              size="small"
              variant="simple"
              >{{ $form.new_password.error?.message }}</Message
            >
          </div>
          <div class="flex flex-col gap-4">
            <label class="font-thin" for="confirm_password">{{
              $t("profile.confirm_password")
            }}</label>
            <Password
              name="confirm_password"
              :placeholder="$t('profile.confirm_password')"
              class="bg-surface-100 rounded-md py-3 px-4 font-thin text-sm"
              v-model="passwordInitialValues.confirm_password"
              :feedback="false"
              toggleMask
            />
            <Message
              v-if="$form.confirm_password?.invalid"
              severity="error"
              size="small"
              variant="simple"
              >{{ $form.confirm_password.error?.message }}</Message
            >
          </div>
        </div>
        <div class="flex justify-center gap-2">
          <Button
            class="bg-white text-primary-900 hover:!bg-primary-900 hover:!text-white px-5 h-12 rounded-lg shadow-md gap-2 border border-primary-900"
            type="submit"
            :label="$t('profile.update')"
          />
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { Avatar, Button, InputText, Message, Password } from "primevue";
import Form from "@primevue/forms/form";
import { z } from "zod";
import { zodResolver } from "@primevue/forms/resolvers/zod";

export default {
  components: {
    Avatar,
    Button,
    InputText,
    Message,
    Form,
    Password,
  },
  data() {
    return {
      resolver: zodResolver(
        z.object({
          fullname: z
            .string()
            .trim()
            .min(1, this.$t("profile.errors.fullname_required")),
          phone: z
            .string()
            .trim()
            .nullable()
            .refine(
              (val) => !val || val.startsWith("996") || val.startsWith("05"),
              {
                message: this.$t("profile.errors.invalid_phone_number"),
              }
            ),
          email: z
            .string()
            .trim()
            .nullable()
            .refine(
              (val) => !val || z.string().email().safeParse(val).success,
              {
                message: this.$t("profile.errors.invalid_email"),
              }
            ),
        })
      ),
      passwordResolver: zodResolver(
        z
          .object({
            old_password: z
              .string()
              .trim()
              .min(1, this.$t("profile.errors.password_required"))
              .regex(
                /^[a-zA-Z0-9_]*$/,
                this.$t("profile.errors.password_characters_numbers")
              )
              .min(10, this.$t("profile.errors.password_length")),
            new_password: z
              .string()
              .trim()
              .min(1, this.$t("profile.errors.password_required"))
              .regex(
                /^[a-zA-Z0-9_]*$/,
                this.$t("profile.errors.password_characters_numbers")
              )
              .min(10, this.$t("profile.errors.password_length")),
            confirm_password: z
              .string()
              .trim()
              .min(1, this.$t("profile.errors.password_required"))
              .regex(
                /^[a-zA-Z0-9_]*$/,
                this.$t("profile.errors.password_characters_numbers")
              )
              .min(10, this.$t("profile.errors.password_length")),
          })
          .refine((ctx) => ctx.new_password === ctx.confirm_password, {
            message: this.$t("profile.errors.passwords_not_matching"),
            path: ["confirm_password"],
          })
      ),
      initialValues: {
        fullname: "",
        phone: "",
        email: "",
      },
      passwordInitialValues: {
        old_password: "",
        new_password: "",
        confirm_password: "",
      },
      passwordFormVisible: false,
      toggleBtn: "0",
      profileFile: null,
      valid: true,
      formPassvalid: true,
      fullname: "",
      email: "",
      phone: "",
      oldPassword: "",
      newPassword: "",
      confPassword: "",
      showOld: false,
      showNew: false,
      showConf: false,
      error: "",
      dialog: false,
      isUploading: false,
      uploadProgress: 0,
      passwordRules: [
        (v) => !!v || this.$t("profile.errors.password_required"),
        (v) =>
          /^[a-zA-Z0-9_]*$/.test(v) ||
          this.$t("profile.errors.password_characters_numbers"),
        (v) =>
          (v && v.length >= 10) || this.$t("profile.errors.password_length"),
      ],
      nameRules: [
        (v) =>
          !!v ||
          v == this.profile.fullname ||
          this.$t("profile.errors.fullname_required"),
      ],
      numberRules: [
        (v) =>
          (v &&
            !isNaN(v) &&
            (v.startsWith("09") ||
              v.startsWith("01") ||
              v.startsWith("249"))) ||
          v == this.profile.phone ||
          this.$t("profile.errors.invalid_phone_number"),
        (v) =>
          (v && v.length >= 10) || this.$t("profile.errors.phone_min_length"),
        (v) =>
          (v && v.length <= 12) || this.$t("profile.errors.phone_max_length"),
      ],
      emailRules: [
        (v) =>
          (v &&
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(
              v
            )) ||
          v == this.profile.email ||
          this.$t("profile.errors.invalid_email"),
      ],
      confpasswordRules: [
        (v) =>
          (v && v == this.newPassword) ||
          this.$t("profile.errors.passwords_not_matching"),
      ],
    };
  },
  methods: {
    togglePasswordForm(toggle) {
      this.passwordInitialValues = {
        old_password: "",
        new_password: "",
        confirm_password: "",
      };
      this.passwordFormVisible = toggle;
    },
    clear(ref) {
      this.$refs[ref].reset();
    },
    async doUpdateProfile({ valid }) {
      if (valid) {
        this.ShowProgressBar({
          show: true,
          message: this.$t("profile.updating_profile"),
        });
        try {
          var resp = await this.EditProfile({
            fullname: this.initialValues.fullname,
            email: this.initialValues.email,
            phone: this.initialValues.phone,
          });
          this.ShowProgressBar({ show: false, message: "" });
          if (!resp.success) {
            this.SetMessageDialog({ show: true, message: resp.message });
          } else {
            this.SetMessageDialog({
              show: true,
              message: resp.message,
              success: resp.success,
            });
          }
        } catch (err) {
          let message = err.message;
          if (err.response) {
            message = err.response.data.message;
          }
          this.ShowProgressBar({ show: false, message: "" });
          this.SetMessageDialog({ show: true, message: message });
        }
      }
    },
    async doResetPassword({ valid }) {
      if (valid) {
        this.ShowProgressBar({
          show: true,
          message: this.$t("profile.resetting_password"),
        });
        try {
          var resp = await this.ResetPassword({
            oldPassword: this.passwordInitialValues.old_password,
            newPassword: this.passwordInitialValues.new_password,
          });
          this.ShowProgressBar({ show: false, message: "" });
          if (!resp.success) {
            this.SetMessageDialog({ show: true, message: resp.message });
          } else {
            this.SetMessageDialog({
              show: true,
              message: resp.message,
              success: resp.success,
            });
          }
        } catch (err) {
          let message = err.message;
          if (err.response) {
            message = err.response.data.message;
          }
          this.ShowProgressBar({ show: false, message: "" });
          this.SetMessageDialog({ show: true, message: message });
        }
      }
    },
    ...mapActions([
      "EditProfile",
      "GetProfile",
      "ResetPassword",
      "SetMessageDialog",
      "ShowProgressBar",
      "UploadProfileImg",
    ]),
    ...mapMutations(["setAvatar"]),
    triggerUpload() {
      this.$refs.fileInput.click();
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.isUploading = true;
      this.uploadProgress = 0;

      try {
        const response = await this.UploadProfileImg({
          file: file,
          filename: file.name,
          filesize: file.size,
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
          },
        });

        if (response.success) {
          await this.GetProfile();
          this.setAvatar(this.profile.avatar);
        } else {
          console.error("Upload failed:", response.message);
        }
      } catch (error) {
        console.error("Upload failed:", error);
      } finally {
        this.isUploading = false;
      }
    },
  },
  computed: {
    ...mapGetters(["profile", "domain"]),
    userGroup() {
      if (!this.profile) {
        return "-";
      } else if (this.profile.type == 1) {
        return this.$t("profile.roles.agent");
      } else if (this.profile.type == 2) {
        return this.$t("profile.roles.reporter");
      } else if (this.profile.type == 3) {
        return this.$t("profile.roles.admin");
      } else {
        return this.$t("profile.roles.super_admin");
      }
    },
    enableEditProfile() {
      if (this.fullname == "") {
        return false;
      }
      if (this.phone != null) {
        if (
          this.phone != "" &&
          (10 > this.phone.length || this.phone.length > 12)
        ) {
          return false;
        }
        if (
          this.phone != "" &&
          !this.phone.startsWith("09") &&
          !this.phone.startsWith("01") &&
          !this.phone.startsWith("249")
        ) {
          return false;
        }
      }
      if (this.email != null) {
        if (
          this.email != "" &&
          !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(
            this.email
          )
        ) {
          return false;
        }
      }
      if (this.phone != null && this.email != null) {
        if (
          this.fullname.trim() == this.profile.fullname &&
          this.phone.trim() == this.profile.phone &&
          this.email.trim() == this.profile.email
        ) {
          return false;
        }
      }
      return true;
    },

    profileAvater() {
      return this.profile
        ? this.profile.avatar
        : require("../assets/user-avatar.png");
    },
  },
  watch: {
    async domain(newValue) {
      if (newValue) {
        await this.GetProfile();
        this.initialValues.fullname = this.profile.fullname;
        this.initialValues.phone = this.profile.phone;
        this.initialValues.email = this.profile.email;
        this.$refs.form.reset();
      }
    },
    profile(newValue) {
      if (newValue) {
        this.initialValues.fullname = this.profile.fullname;
        this.initialValues.phone = this.profile.phone;
        this.initialValues.email = this.profile.email;
      }
    },
  },
  async mounted() {
    if (this.domain) {
      await this.GetProfile();
      this.initialValues.fullname = this.profile.fullname;
      this.initialValues.phone = this.profile.phone;
      this.initialValues.email = this.profile.email;
      this.$refs.form.reset();
    }
  },
};
</script>

<style scoped>
.green--text {
  color: #006c54 !important;
}
.fill-height {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.primary {
  color: #ffffff !important;
}
.bg-green {
  background-color: #006c54 !important;
}
.profile-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.profile-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}
.v-text-field {
  background-color: #f9f9f9;
  border-radius: 8px;
}
.v-text-field .v-input__control {
  padding: 12px;
}
.enhanced-btn {
  margin: 10px 5px 0 5px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
.enhanced-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}
.header-effect {
  position: relative;
  display: inline-block;
}
.header-effect::after {
  content: "";
  position: absolute;
  width: 100%;
  transform: scaleX(0);
  height: 3px;
  bottom: 0;
  left: 0;
  background-color: #006c54;
  transform-origin: bottom right;
  transition: transform 0.25s ease-out;
}
.header-effect:hover::after {
  transform: scaleX(1);
  transform-origin: bottom left;
}
.center-title {
  text-align: center;
}
.v-avatar img {
  border-radius: 50%;
  object-fit: contain;
}
.v-avatar {
  cursor: pointer; /* Makes avatar clickable */
}
.avatar-image {
  width: 100%; /* Ensures the image fills the avatar */
  height: 100%; /* Ensures the image fills the avatar */
  object-fit: contain; /* Adjusts to fit the whole image within the avatar */
  border-radius: 50%; /* Keeps the circular shape */
}

.avatar-container {
  position: relative;
  display: inline-block;
}

.progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
